import { Controller } from 'stimulus';
import Chart from 'chart.js/auto';
import { geoMercator, geoIdentity, geoPath } from 'd3-geo';

const CHART_COLORS = {
  red: 'rgb(255, 99, 132)',
  orange: 'rgb(255, 159, 64)',
  yellow: 'rgb(255, 205, 86)',
  green: 'rgb(75, 192, 192)',
  blue: 'rgb(54, 162, 235)',
  purple: 'rgb(153, 102, 255)',
  grey: 'rgb(201, 203, 207)',
  lightGrey: 'rgb(238, 242, 247)',
};

let swissBrickMapPromise;
let swissCantonMapPromise;
const MAP_WIDTH = 860;
const MAP_HEIGHT = 540;
const MAP_COLORS = ['#dbeafe', '#93c5fd', '#60a5fa', '#2563eb', '#1d4ed8'];

async function loadSwissBrickMap() {
  if (!swissBrickMapPromise) {
    swissBrickMapPromise = fetch('/maps/ch-bricks.json').then((response) => response.json());
  }

  return swissBrickMapPromise;
}

async function loadSwissCantonMap() {
  if (!swissCantonMapPromise) {
    swissCantonMapPromise = fetch('/maps/ch-cantons-heatmap.json').then((response) => response.json());
  }

  return swissCantonMapPromise;
}

export default class extends Controller {
  static values = { institutionId: Number, eventId: Number, translations: Object }

  connect() {
    this.render_video_watchers_by_channel_chart();
    this.render_video_watchers_by_ownership_chart();
    this.render_event_listeners_chart();
    this.render_video_watchers_heatmap();
    this.render_event_listeners_heatmap();
  }

  render_video_watchers_by_ownership_chart() {
    const ctx = document.getElementById('video-watchers-by-ownership');
    if (!ctx) return;

    this.getOwnershipData().then((data) => {
      const chartData = {
        labels: [
          this.translationsValue.video_watchers_by_ownership.owned_by_institution,
          this.translationsValue.video_watchers_by_ownership.owned_by_others,
        ],
        datasets: [{
          barThickness: 30,
          data: [data.owned_by_institution, data.owned_by_others],
          backgroundColor: [CHART_COLORS.green, CHART_COLORS.blue],
        }],
      };

      const config = {
        type: 'bar',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
              position: 'right',
            },
            title: {
              display: false,
              text: 'Video Watchers by Ownership',
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      };

      new Chart(ctx, config);
    });
  }

  async getOwnershipData() {
    const response = await fetch(`/admin/institutions/${this.institutionIdValue}/stats/video_watchers_by_ownership`);
    return response.json();
  }

  render_video_watchers_by_channel_chart() {
    const ctx = document.getElementById('video-watchers-by-channel');
    if (!ctx) return;

    this.getData().then((data) => {
      data.datasets[0].backgroundColor = this.pieChartColors();

      const config = {
        type: 'pie',
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right',
              labels: {
                usePointStyle: true,
              },
            },
            title: {
              display: false,
              text: 'Viewers by Speciality',
              position: 'top',
            },
          },
        },
      };

      new Chart(ctx, config);
    });
  }

  render_video_watchers_timeseries_chart() {
    const ctx = document.getElementById('video-watchers');
    if (!ctx) return;

    this.getData().then((data) => {
      const config = {
        type: 'line',
        data: data,
        options: {
          plugins: {
            title: {
              display: false,
              text: 'Watchers',
            },
          },
          responsive: true,
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
            },
          },
        },
      };

      new Chart(ctx, config);
    });
  }

  pieChartColors() {
    return Object.values(CHART_COLORS).slice(0, 6);
  }

  async getData() {
    const response = await fetch(`/admin/institutions/${this.institutionIdValue}/stats/video_watchers_by_channel`);
    return response.json();
  }

  render_event_listeners_chart() {
    const ctx = document.getElementById('event-listeners-by-event');
    if (!ctx) return;

    this.getEventListenersData().then((data) => {
      const chartData = {
        labels: data.map((d) => new Date(d.event_date).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        })),
        datasets: [{
          label: 'Subscribers',
          barThickness: 30,
          data: data.map((d) => d.event_listeners_count),
          backgroundColor: CHART_COLORS.blue,
        }],
      };

      const config = {
        type: 'bar',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
              position: 'right',
            },
            title: {
              display: false,
              text: 'Event Subscribers Count',
            },
            tooltip: {
              callbacks: {
                title: (context) => data[context[0].dataIndex].event_headline,
                label: (context) => `Subscribers: ${context.formattedValue}`,
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      };

      new Chart(ctx, config);
    });
  }

  async getEventListenersData() {
    const response = await fetch(`/admin/institutions/${this.institutionIdValue}/stats/event_listeners_stats_by_event`);
    return response.json();
  }

  render_video_watchers_heatmap() {
    this.render_heatmap({
      containerId: 'video-watchers-by-brick',
      noteId: 'video-watchers-by-brick-note',
      tooltipLabel: this.translationsValue.video_watchers.headline,
      modeOrder: ['canton', 'brick'],
      initialMode: 'canton',
      fetchers: {
        brick: () => this.getVideoWatchersByBrickData(),
        canton: () => this.getVideoWatchersByCantonData(),
      },
    });
  }

  render_event_listeners_heatmap() {
    this.render_heatmap({
      containerId: 'event-listeners-by-brick',
      noteId: 'event-listeners-by-brick-note',
      tooltipLabel: this.translationsValue.event_listeners.headline,
      modeOrder: ['canton', 'brick'],
      initialMode: 'canton',
      fetchers: {
        brick: () => this.getEventListenersByBrickData(),
        canton: () => this.getEventListenersByCantonData(),
      },
    });
  }

  // Describes how each heatmap mode loads its map, projects it, keys its data
  // and labels itself. `projected: true` means the map is already in planar
  // screen space (geoIdentity); otherwise coordinates are WGS84 (geoMercator).
  heatmapModes() {
    const t = this.translationsValue;
    return {
      brick: {
        loadMap: loadSwissBrickMap,
        projected: false,
        featureId: (feature) => feature.properties.brick_id,
        featureName: (feature) => feature.properties.brick_name,
        entryKey: (entry) => entry.brick_id,
        label: t.heatmap_view_brick,
        scopeNote: t.brick_heatmap_scope_note,
        excludedNote: t.brick_heatmap_excluded_records,
        noData: t.brick_heatmap_no_data,
      },
      canton: {
        loadMap: loadSwissCantonMap,
        projected: true,
        featureId: (feature) => feature.properties.canton_code,
        featureName: (feature) => feature.properties.canton_name,
        entryKey: (entry) => entry.canton,
        label: t.heatmap_view_canton,
        scopeNote: t.canton_heatmap_scope_note,
        excludedNote: t.canton_heatmap_excluded_records,
        noData: t.canton_heatmap_no_data,
      },
    };
  }

  render_heatmap({
    containerId,
    noteId,
    tooltipLabel,
    fetchers,
    modeOrder = ['brick', 'canton'],
    initialMode = modeOrder[0],
  }) {
    const container = document.getElementById(containerId);
    const note = document.getElementById(noteId);
    if (!container || !note) return;

    const modes = this.heatmapModes();
    const statsCache = {};
    let activeMode = initialMode;

    container.innerHTML = `
      <div class="admin-canton-map__toggle" role="group">
        ${modeOrder.map((modeName) => `
          <button type="button" class="admin-canton-map__toggle-btn${modeName === initialMode ? ' is-active' : ''}" data-mode="${this.escapeHtml(modeName)}">${this.escapeHtml(modes[modeName].label)}</button>
        `).join('')}
      </div>
      <div class="admin-canton-map__view"></div>
    `;

    const view = container.querySelector('.admin-canton-map__view');
    const buttons = Array.from(container.querySelectorAll('.admin-canton-map__toggle-btn'));

    const show = async (modeName) => {
      activeMode = modeName;
      buttons.forEach((button) => button.classList.toggle('is-active', button.dataset.mode === modeName));
      if (!statsCache[modeName]) statsCache[modeName] = fetchers[modeName]();
      await this.render_heatmap_mode({
        mode: modes[modeName],
        view,
        note,
        tooltipLabel,
        stats: await statsCache[modeName],
      });
    };

    buttons.forEach((button) => button.addEventListener('click', () => {
      if (button.dataset.mode !== activeMode) show(button.dataset.mode);
    }));

    show(initialMode);
  }

  async render_heatmap_mode({ mode, view, note, tooltipLabel, stats }) {
    const { features, outline } = await mode.loadMap();

    const entriesById = stats.entries.reduce((result, entry) => {
      result[mode.entryKey(entry)] = entry;
      return result;
    }, {});

    const totalCount = stats.entries.reduce((sum, entry) => sum + entry.count, 0);
    if (totalCount === 0) {
      note.textContent = mode.noData;
      view.innerHTML = `<div class="admin-canton-map__empty">${this.escapeHtml(mode.noData)}</div>`;
      return;
    }

    note.textContent = this.excludedMessage(mode, stats.excluded_count);

    const svgMarkup = this.buildMapSvg({ mode, features, outline, tooltipLabel, entriesById });
    const legendMarkup = this.buildMapLegend(entriesById);
    view.innerHTML = `
      <div class="admin-canton-map__frame">
        ${svgMarkup}
      </div>
      ${legendMarkup}
    `;

    this.attachHeatmapPopover(view, tooltipLabel, entriesById);
  }

  // Wires up a hover/click popover for the rendered map. A single popover node
  // lives inside the frame, follows the cursor on hover, and pins on click
  // (click the region again, or any empty map area, to dismiss).
  attachHeatmapPopover(view, tooltipLabel, entriesById) {
    const frame = view.querySelector('.admin-canton-map__frame');
    const svg = view.querySelector('.admin-canton-map__svg');
    if (!frame || !svg) return;

    const popover = document.createElement('div');
    popover.className = 'admin-canton-map__popover';
    popover.setAttribute('role', 'tooltip');
    popover.hidden = true;
    frame.appendChild(popover);

    let pinned = null;

    const fill = (path) => {
      const name = path.getAttribute('data-region-name') || '';
      const count = path.getAttribute('data-region-count') || '0';
      const entry = entriesById[path.getAttribute('data-region-id')];
      const specialties = (entry && entry.specialties) || [];

      const breakdown = specialties.length
        ? `<ul class="admin-canton-map__popover-breakdown">${
          specialties.map((item) => `
            <li>
              <span class="admin-canton-map__popover-breakdown-name">${this.escapeHtml(item.name)}</span>
              <span class="admin-canton-map__popover-breakdown-count">${this.escapeHtml(String(item.count))}</span>
            </li>
          `).join('')
        }</ul>`
        : '';

      popover.innerHTML = `
        <span class="admin-canton-map__popover-title">${this.escapeHtml(name)}</span>
        <span class="admin-canton-map__popover-metric"><strong>${this.escapeHtml(count)}</strong> ${this.escapeHtml(tooltipLabel)}</span>
        ${breakdown}
      `;
    };

    const position = (event) => {
      const bounds = frame.getBoundingClientRect();
      const pw = popover.offsetWidth;
      const ph = popover.offsetHeight;
      let x = event.clientX - bounds.left + 14;
      let y = event.clientY - bounds.top + 14;
      if (x + pw > bounds.width) x = event.clientX - bounds.left - pw - 14;
      if (y + ph > bounds.height) y = event.clientY - bounds.top - ph - 14;
      popover.style.left = `${Math.max(8, x)}px`;
      popover.style.top = `${Math.max(8, y)}px`;
    };

    const showFor = (path, event) => {
      fill(path);
      popover.hidden = false;
      position(event);
    };

    svg.addEventListener('mouseover', (event) => {
      if (pinned) return;
      const path = event.target.closest('.admin-canton-map__canton');
      if (path) showFor(path, event);
    });

    svg.addEventListener('mousemove', (event) => {
      if (pinned || popover.hidden) return;
      position(event);
    });

    svg.addEventListener('mouseout', (event) => {
      if (pinned) return;
      const from = event.target.closest('.admin-canton-map__canton');
      const to = event.relatedTarget && event.relatedTarget.closest
        ? event.relatedTarget.closest('.admin-canton-map__canton')
        : null;
      if (from && !to) popover.hidden = true;
    });

    svg.addEventListener('click', (event) => {
      const path = event.target.closest('.admin-canton-map__canton');

      if (pinned) pinned.classList.remove('is-pinned');

      // Clicking empty map area, or the already-pinned region, clears the pin.
      if (!path || path === pinned) {
        pinned = null;
        popover.classList.remove('is-pinned');
        popover.hidden = true;
        return;
      }

      pinned = path;
      path.classList.add('is-pinned');
      popover.classList.add('is-pinned');
      showFor(path, event);
    });
  }

  buildMapSvg({ mode, features, outline, tooltipLabel, entriesById }) {
    const extent = [[24, 24], [MAP_WIDTH - 24, MAP_HEIGHT - 24]];
    const projection = mode.projected
      ? geoIdentity().fitExtent(extent, outline)
      : geoMercator().fitExtent(extent, outline);
    const pathGenerator = geoPath(projection);
    const outlinePath = pathGenerator(outline);
    const maxValue = Math.max(...Object.values(entriesById).map((entry) => entry.count), 0);

    const regionPaths = features.map((feature) => {
      const regionId = mode.featureId(feature);
      const entry = entriesById[regionId];
      const count = entry ? entry.count : 0;
      const pathData = pathGenerator(feature);
      const regionName = mode.featureName(feature);

      return `
        <path
          class="admin-canton-map__canton"
          role="img"
          aria-label="${this.escapeHtml(`${regionName}: ${count} ${tooltipLabel}`)}"
          data-region-id="${this.escapeHtml(regionId)}"
          data-region-name="${this.escapeHtml(regionName)}"
          data-region-count="${this.escapeHtml(String(count))}"
          fill="${this.regionMapColor(count, maxValue)}"
          d="${pathData}"
        ></path>
      `;
    }).join('');

    return `
      <svg
        class="admin-canton-map__svg"
        viewBox="0 0 ${MAP_WIDTH} ${MAP_HEIGHT}"
        role="img"
        aria-label="${this.escapeHtml(tooltipLabel)}"
        preserveAspectRatio="xMidYMid meet"
      >
        <path class="admin-canton-map__outline" d="${outlinePath}" />
        ${regionPaths}
      </svg>
    `;
  }

  buildMapLegend(entriesById) {
    const values = Object.values(entriesById).map((entry) => entry.count).filter((value) => value > 0);
    if (!values.length) return '';

    const maxValue = Math.max(...values);
    const step = Math.max(1, Math.ceil(maxValue / MAP_COLORS.length));

    const items = [
      { label: '0', color: CHART_COLORS.lightGrey },
      ...MAP_COLORS.map((color, index) => {
        const start = (index * step) + 1;
        const end = Math.min(maxValue, (index + 1) * step);

        if (start > maxValue) return null;

        return {
          label: start === end ? `${start}` : `${start}-${end}`,
          color,
        };
      }).filter(Boolean),
    ];

    const legendItems = items.map((item) => `
      <li class="admin-canton-map__legend-item">
        <span class="admin-canton-map__legend-swatch" style="background:${item.color}"></span>
        <span>${this.escapeHtml(item.label)}</span>
      </li>
    `).join('');

    return `<ul class="admin-canton-map__legend">${legendItems}</ul>`;
  }

  regionMapColor(value, maxValue) {
    if (value <= 0 || maxValue <= 0) return CHART_COLORS.lightGrey;

    const step = Math.max(1, Math.ceil(maxValue / MAP_COLORS.length));
    const colorIndex = Math.min(
      MAP_COLORS.length - 1,
      Math.floor((value - 1) / step),
    );

    return MAP_COLORS[colorIndex];
  }

  excludedMessage(mode, excludedCount) {
    if (!excludedCount) return mode.scopeNote;
    return mode.excludedNote.replace('%{count}', excludedCount);
  }

  async getVideoWatchersByBrickData() {
    const response = await fetch(`/admin/institutions/${this.institutionIdValue}/stats/video_watchers_by_brick`);
    return response.json();
  }

  async getEventListenersByBrickData() {
    const path = this.hasEventIdValue
      ? `/admin/events/${this.eventIdValue}/event_listeners/by_brick`
      : `/admin/institutions/${this.institutionIdValue}/stats/event_listeners_by_brick`;

    const response = await fetch(path);
    return response.json();
  }

  async getVideoWatchersByCantonData() {
    const response = await fetch(`/admin/institutions/${this.institutionIdValue}/stats/video_watchers_by_canton`);
    return response.json();
  }

  async getEventListenersByCantonData() {
    const path = this.hasEventIdValue
      ? `/admin/events/${this.eventIdValue}/event_listeners/by_canton`
      : `/admin/institutions/${this.institutionIdValue}/stats/event_listeners_by_canton`;

    const response = await fetch(path);
    return response.json();
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}
